import { firstValueFrom, retry } from 'rxjs';
import { container } from '../context/module';
import { FileUpload } from '../models/file-upload';
import { FileUploadRequestMetadata } from '../models/file-upload-request-metadata';
import { UploadInProgressFile, UploadInProgressFileFactory } from '../models/upload-in-progress-file';
import { ApiConfig } from '../services/api-config';
import { LightHttp } from '../services/light-http.service';
import { ConvertOptionCategories } from '../types/dropdown.type';
import { PageData } from '../types/route-types';
import { FromType, ProcessType, StatusTypes, ToType } from '../types/types';
import { getExtension } from './file';
import { cloneDeep } from './lodash-min';
import { StringUtils } from './string-utils';

export class FileUploadUtils {
    public static async newFileId(): Promise<string> {
        const apiConfig: ApiConfig = container.get(ApiConfig);
        const http: LightHttp = container.get(LightHttp);
        const uploadInProgressFile = http.get<{ id: string }>(apiConfig.generateFileId()).pipe(
            retry(5),
        );
        const fileIdResp = await firstValueFrom(uploadInProgressFile);
        return fileIdResp.id;
    }

    public static async toUploadInProgressFile<T>(
        fileUpload: FileUpload,
    ): Promise<UploadInProgressFile<T>[]> {
        const inProgressFiles: UploadInProgressFile<T>[] = [];
        for (const originalInputFile of fileUpload.originalInputFiles) {
            const uploadInProgressFile: UploadInProgressFile<T> =
                await UploadInProgressFileFactory.newUploadInProgressFile(
                    null,
                    null,
                    fileUpload.uniqueProcessType,
                );
            uploadInProgressFile.file = {
                lastModified: originalInputFile.metadata.flastModified,
                name: originalInputFile.metadata.fname,
                size: originalInputFile.metadata.fsize,
                type: originalInputFile.metadata.ftype,
                slice: () => null,
            } as any;
            if (fileUpload.metadataList && fileUpload.metadataList.length > 0) {
                uploadInProgressFile.metadata = fileUpload.metadataList.find(l => {
                    // GCS upload removed — inputs are always local, matched by safeName.
                    return l.processorMetadataList[0].fileMetadata.safeName === originalInputFile.location;
                });
            } else {
                uploadInProgressFile.metadata = fileUpload.metadata;
            }

            uploadInProgressFile.metadata = new FileUploadRequestMetadata(
                uploadInProgressFile.metadata.processorMetadataList,
                uploadInProgressFile.metadata.file,
            );

            uploadInProgressFile.uuid = StringUtils.uuid();
            uploadInProgressFile.progress = 100;
            uploadInProgressFile.completed = true;
            uploadInProgressFile.error = false;
            uploadInProgressFile.setResponse(originalInputFile);
            uploadInProgressFile.orientation = { angle: '0', isFliped: 'false', };

            uploadInProgressFile.setComplete();
            inProgressFiles.push(uploadInProgressFile)

        }
        return inProgressFiles;
    }

    public static toFileUpload<T>(
        uploadInProgressFile: UploadInProgressFile<T>,
        wsId: string,
        processType: ProcessType,
        fromType: FromType[],
        toType: ToType,
        options: any,
        optionsSnapshot: ConvertOptionCategories,
        uniqueProcessType: string,
        pageData?: PageData
    ): FileUpload {
        const fileUpload = new FileUpload();

        fileUpload.id = StringUtils.uuid();
        fileUpload.wsId = wsId;
        fileUpload.uploadStartTime = new Date();
        fileUpload.progress = 0;
        fileUpload.result = undefined;
        fileUpload.zipResult = undefined;
        fileUpload.status = StatusTypes.UNDEFINED;
        fileUpload.name = (uploadInProgressFile.file as any).name;
        fileUpload.size = (uploadInProgressFile.file as any).size;
        fileUpload.file = uploadInProgressFile.file as any;
        fileUpload.readableMessage = undefined;
        fileUpload.ws = undefined;
        fileUpload.processType = processType;
        fileUpload.fromType = fromType;
        fileUpload.extension = [getExtension((uploadInProgressFile.file as any).name)];
        fileUpload.toType = toType;
        fileUpload.options = options;
        fileUpload.discartWSReply = false;
        fileUpload.originalInputFiles = [uploadInProgressFile.getResponse()];
        fileUpload.endpointData = uploadInProgressFile.getMetadata();
        fileUpload.settings = {} as any;
        // TODO handle uploadInProgressFile
        fileUpload.settings.email = '';
        fileUpload.processStartTime = new Date();
        fileUpload.optionsSnapshot = optionsSnapshot;
        fileUpload.metadata = uploadInProgressFile.getMetadata();
        fileUpload.uniqueProcessType = uniqueProcessType;
        fileUpload.pageData = pageData
        return fileUpload;
    }
    public static createMergeFileUpload<T>(
        uploadInProgressFiles: UploadInProgressFile<T>[],
        wsId: string,
        processType: ProcessType,
        fromType: FromType[],
        toType: ToType,
        options: any,
        optionsSnapshot: any,
        pageData?: PageData
    ): FileUpload {
        const fileUploads: FileUpload[] = uploadInProgressFiles.map(fmd => {
            return FileUploadUtils.toFileUpload(
                fmd,
                wsId,
                processType,
                fromType,
                toType,
                options,
                optionsSnapshot,
                fmd.uniqueProcessType,
                pageData
            );
        });

        const fileUpload = fileUploads.splice(0, 1)[0];
        fileUpload.metadataList = [cloneDeep(fileUpload.metadata)];
        fileUploads.forEach(fu => {
            fileUpload.originalInputFiles.push(...fu.originalInputFiles);
            fileUpload.metadataList.push(cloneDeep(fu.metadata));
        });

        fileUpload.result = undefined;
        fileUpload.zipResult = undefined;
        return fileUpload;
    }

    public static createSingleFileUploads<T>(
        uploadInProgressFiles: UploadInProgressFile<T>[],
        wsId: string,
        processType: ProcessType,
        fromType: FromType[],
        toType: ToType,
        baseOptions: any,
        pageData?: PageData
    ): FileUpload[] {
        return uploadInProgressFiles.map(fmd => {
            const options = cloneDeep(baseOptions);
            const orientationAngleList: string[] = [(fmd.getOrientation() && fmd.getOrientation().angle) || '0'];
            const orientationFlipList: string[] = [(fmd.getOrientation() && fmd.getOrientation().isFliped) || 'false'];

            options.orientationAngleList = orientationAngleList;
            options.orientationFlipList = orientationFlipList;

            const fileUpload = FileUploadUtils.toFileUpload(
                fmd,
                wsId,
                processType,
                fromType,
                toType,
                options,
                null,
                fmd.uniqueProcessType,
                pageData,
            );

            fileUpload.metadataList = [cloneDeep(fileUpload.metadata)];
            fileUpload.originalInputFiles = cloneDeep(fileUpload.originalInputFiles);
            fileUpload.result = undefined;
            fileUpload.zipResult = undefined;
            return fileUpload;
        });
    }
}
